import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { AssetCategory } from '../../sj-orm/models/asset.dto';
import {
  ResidentialRealEstateDetails,
  ResidentialRealEstateGeneral,
} from '../../sj-orm/models/assets/categories';
import { ResidentialRealEstateType } from '../../sj-orm/models/assets/enums';
import { ContactType } from '../../sj-orm/models/contact.dto';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder } from './builders/statistics';
import { makeDtoFilter } from './filters/makeDtoFilter';
import { baseAssetFields } from './fields';

export type ResidentialRealEstate = ResidentialRealEstateFormGeneralType &
  ResidentialRealEstateFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const ResidentialRealEstateList = [
  {
    label: 'forms_assets:residentialRealEstate.enums.type.flat',
    key: ResidentialRealEstateType.flat,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.studio',
    key: ResidentialRealEstateType.studio,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.apartment',
    key: ResidentialRealEstateType.apartment,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.condominium',
    key: ResidentialRealEstateType.condominium,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.house',
    key: ResidentialRealEstateType.house,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.cottage',
    key: ResidentialRealEstateType.cottage,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.duplex',
    key: ResidentialRealEstateType.duplex,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.bungalow',
    key: ResidentialRealEstateType.bungalow,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.chalet',
    key: ResidentialRealEstateType.chalet,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.townhouse',
    key: ResidentialRealEstateType.townhouse,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.ranch',
    key: ResidentialRealEstateType.ranch,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.penthouse',
    key: ResidentialRealEstateType.penthouse,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.villa',
    key: ResidentialRealEstateType.villa,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.mansion',
    key: ResidentialRealEstateType.mansion,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.castle',
    key: ResidentialRealEstateType.castle,
  },
  {
    label: 'forms_assets:residentialRealEstate.enums.type.estate',
    key: ResidentialRealEstateType.estate,
  },
];

type ResidentialRealEstateHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
};

type ResidentialRealEstateHelpDetails = {
  residentialRealEstateNumberName?: QuestionType.TEXT;
  residentialRealEstateNumber?: QuestionType.TEXT;
  // otherAddressInfo?: QuestionType.TEXT_MULTILINE;
  constructionDate?: QuestionType.DATE_TIME;
  apartmentSquare?: QuestionType.NUMBER;
  compositionOfObjects?: QuestionType.TEXT;
  landPlot?: QuestionType.CHECKBOX;
  chooseLandPlot?: QuestionType.PICK_ARRAY_OF_DTO;
  contacts?: QuestionType.PICK_ARRAY_OF_DTO;
  address?: QuestionType.ADDRESS;
  attachments?: QuestionType.FILE_UPLOAD;
};

type ResidentialRealEstateFormGeneralType = TDtoWrapper<
  ResidentialRealEstateGeneral,
  ResidentialRealEstateHelpGeneral
>;

type ResidentialRealEstateFormDetailsType = TDtoWrapper<
  ResidentialRealEstateDetails,
  ResidentialRealEstateHelpDetails
>;

const ResidentialRealEstateFormGeneral: ResidentialRealEstateFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:residentialRealEstate.inputs.type.label',
    value: ResidentialRealEstateType.apartment,
    items: ResidentialRealEstateList,
    required: true,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:residentialRealEstate.inputs.currentMarketPrice.label',
    tooltip: 'forms_assets:residentialRealEstate.inputs.currentMarketPrice.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
};

const ResidentialRealEstateFormDetails: ResidentialRealEstateFormDetailsType = {
  residentialRealEstateNumberLabel: {
    type: FormLabelItem.label,
    label: 'forms_assets:common.inputs.realEstateNumberLabel.label',
    order: 1,
  },
  residentialRealEstateNumberName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:residentialRealEstate.inputs.residentialRealEstateNumberName.label',
    order: 2,
  },
  residentialRealEstateNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:residentialRealEstate.inputs.residentialRealEstateNumber.label',
    order: 3,
  },
  commencementDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:residentialRealEstate.inputs.commencementDate.label',
    tooltip: 'forms_assets:residentialRealEstate.inputs.commencementDate.tooltip',
    order: 4,
  },
  areaOfTheApartments: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:residentialRealEstate.inputs.areaOfTheApartments.label',
    order: 5,
  },
  compositionOfObjects: {
    type: QuestionType.TEXT,
    label: 'forms_assets:residentialRealEstate.inputs.compositionOfObjects.label',
    order: 6,
  },
  landPlot: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:residentialRealEstate.inputs.landPlot.label',
    order: 7,
  },
  chooseLandPlot: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:residentialRealEstate.inputs.chooseLandPlot.label',
    items: CollectionName.ASSETS,
    showIf: {
      landPlot: {
        value: true,
      },
    },
    makeExtFilter: makeDtoFilter({
      categories: [AssetCategory.LAND],
      withCountryFilter: true,
    }),
    deps: ['country'],
    dtoType: AssetCategory.LAND,
    order: 8,
  },
  contacts: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:common.inputs.contacts.label',
    items: CollectionName.CONTACTS,
    mode: 'multiple',
    dtoType: [
      ContactType.INDIVIDUAL,
      ContactType.LEGAL_ENTITY,
      ContactType.BROKER,
      ContactType.BANK,
    ],
    order: 9,
  },
  address: {
    type: QuestionType.ADDRESS,
    label: 'forms_assets:common.inputs.address.label',
    tooltip: 'forms_assets:common.inputs.address.tooltip',
    value: undefined,
    order: 9,
  },
  attachments: baseAssetFields.attachments,
};

const ResidentialRealEstateForm = [
  ResidentialRealEstateFormGeneral,
  ResidentialRealEstateFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { ResidentialRealEstateForm };
